import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import {
    type ContentOwnership,
    compareContentOwners,
    isSoftDeletedSpace,
} from '@gitbook/app-database';
import { translationLanguageDescriptors } from '@gitbook/app-translations';
import type { PaletteCommandDef, PaletteGroupDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    Dialog,
    DialogActionButton,
    type DialogActionButtonProps,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    Heading,
    intlPluralizeString,
    toast,
    useDialogClose,
    useDialogTriggerState,
} from '@gitbook/spine-react';
import { useEventCallback, usePersistentValue } from '@gitbook/util-react-hooks';
import { filterOutNullable } from '@gitbook/util-ts';

import { useNavigation } from '../../navigation';
import { apiClient, apiQuery, type FeatureUpgradeAction, useGetUpgradeAction } from '../../state';
import { capitalizeFirstLetter } from '../../utils';
import { createPickContentOwnerPalette } from '../Content';
import { useCreateInOwner } from '../ContentListing';
import { useCopyCommands } from '../Link/useCopyCommands';
import { useOrganizationSites } from '../Organization';
import { createPickLanguagePalette } from '../Translations';
import { SpaceCreationDialog } from './creation';
import { DuplicateSpaceDialogContent } from './DuplicateSpaceDialogContent';
import { UpdateSpaceTitleDialog } from './UpdateSpaceTitleDialog';

type SpaceAction = 'edit' | 'duplicate' | 'language' | 'permissions' | 'moveTo' | 'copy' | 'delete';

type SpaceSiteContext = {
    organization?: api.Organization;
    site?: api.Site;
    siteId?: string;
    sitePermissions?: api.Site['permissions'];
    siteSpaceId?: string;
    siteItemType?: 'variant' | 'section';
    renameSiteItem?: () => void;
};

type DuplicateSpaceSiteContext = {
    siteId: string;
    sitePermissions: api.Site['permissions'];
    siteSpaceId: string;
    siteItemType: 'variant' | 'section';
};

const ALL_SPACE_ACTIONS: ReadonlyArray<SpaceAction> = [
    'edit',
    'duplicate',
    'language',
    'permissions',
    'moveTo',
    'copy',
    'delete',
];

/**
 * Return common basic actions commands for a space.
 *
 * `siteContext.siteId` is the site the space is being acted on within, if any.
 * It lets the delete flow offer to detach a `styleguide` space from that specific
 * site instead of blocking outright.
 */
export function useSpaceActionsCommands(
    space: api.Space,
    actions?: [SpaceAction, ...SpaceAction[]],
    siteContext?: SpaceSiteContext
): {
    paletteGroup: () => PaletteGroupDef;
    dialogs: React.ReactNode;
} {
    const selectedActions = actions ?? ALL_SPACE_ACTIONS;

    const copyPalette = useSpaceCopyCommands(space);
    const updateTitleTrigger = useDialogTriggerState();
    const [confirmDialogState, setConfirmDialogState] = React.useState<ConfirmDialogState | null>(
        null
    );
    const { open: renameSpace } = updateTitleTrigger;
    const renameSiteItem = siteContext?.renameSiteItem ?? null;
    const renameTitle = getRenameCommandTitle(siteContext);
    const canAdminSite = siteContext?.sitePermissions?.admin === true;
    const upgrade = useGetUpgradeAction(
        siteContext?.organization && siteContext.site && siteContext.siteItemType === 'section'
            ? {
                  organization: siteContext.organization,
                  site: siteContext.site,
                  featureKey: api.SiteFeatureKey.SitesSections,
              }
            : undefined
    );
    const duplicateSiteContext =
        siteContext?.siteId &&
        siteContext.sitePermissions &&
        siteContext.siteSpaceId &&
        siteContext.siteItemType
            ? {
                  siteId: siteContext.siteId,
                  sitePermissions: siteContext.sitePermissions,
                  siteSpaceId: siteContext.siteSpaceId,
                  siteItemType: siteContext.siteItemType,
              }
            : undefined;
    const duplicateSpace = useDuplicateSpaceCommand(space, duplicateSiteContext, upgrade);
    const canRename = renameSiteItem ? canAdminSite : space.permissions.admin;

    const deleteSpace = useEventCallback(async () => {
        await toast.catch(async () => {
            await apiClient.spaces.deleteSpaceById(space.id);
            const toastId = toast.success('Space deleted', {
                description: 'You can restore this space within 7 days.',
                action: (
                    <Button
                        label="Undo"
                        onPress={async () => {
                            await toast.catch(async () => {
                                await apiClient.spaces.restoreSpace(space.id);
                                toast.dismiss(toastId);
                            }, 'Error while restoring space');
                        }}
                    />
                ),
            });
        }, 'Error while deleting space');
    });

    const moveSpace = useEventCallback(async (targetOwner: ContentOwnership) => {
        const promise = (async () => {
            await apiClient.spaces.moveSpace(space.id, {
                parent: targetOwner.collection || null,
            });

            analytics.logEvent(
                analytics.createSpaceEvent(
                    space,
                    analytics.createContentMoveEvent(
                        {
                            organization: space.organization,
                            collection: space.parent,
                        },
                        targetOwner,
                        'palette',
                        {
                            type: 'space_move',
                        }
                    )
                )
            );
        })();

        toast.promise(promise, {
            loading: 'Moving space…',
            success: 'Space moved',
            error: 'Error while moving space',
        });

        return promise;
    });

    const paletteGroup = useEventCallback((): PaletteGroupDef => {
        const commands = isSoftDeletedSpace(space)
            ? selectedActions.includes('copy')
                ? [copyPalette()]
                : []
            : [
                  selectedActions.includes('language') ? getSpaceLanguageCommands(space) : null,
                  // Move Space — not offered for `styleguide` spaces, which are
                  // bound to the site settings that reference them.
                  space.permissions.admin &&
                  space.type !== 'styleguide' &&
                  selectedActions.includes('moveTo')
                      ? {
                            key: 'space.moveTo',
                            icon: Icon.ArrowCurveUpRight,
                            title: 'Move space to…',
                            placeholder: 'No content found',
                            descendant: () => {
                                return createPickContentOwnerPalette({
                                    filter: (ancestors) =>
                                        ancestors[0]?.organization === space.organization,
                                    onPick: (owner) => {
                                        if (
                                            // Test if we are moving to the same location
                                            !compareContentOwners(
                                                {
                                                    organization: space.organization,
                                                    collection: space.parent,
                                                },
                                                owner,
                                                true
                                            )
                                        ) {
                                            if (owner.organization === space.organization) {
                                                moveSpace(owner);
                                            } else {
                                                setConfirmDialogState({
                                                    space,
                                                    action: 'move',
                                                    onConfirm: async () => {
                                                        await moveSpace(owner);
                                                    },
                                                });
                                            }
                                        }
                                    },
                                });
                            },
                        }
                      : null,
                  // Copy commands
                  selectedActions.includes('copy') ? copyPalette() : null,
                  // Duplicate
                  selectedActions.includes('duplicate') ? duplicateSpace.command() : null,
                  selectedActions.includes('edit') && canRename
                      ? {
                            key: 'space.rename',
                            icon: Icon.Edit,
                            title: renameTitle,
                            upgrade: Boolean(renameSiteItem && upgrade),
                            trigger:
                                (renameSiteItem && upgrade?.onPress) ??
                                renameSiteItem ??
                                renameSpace,
                        }
                      : null,
                  // Delete space
                  selectedActions.includes('delete') && space.permissions.admin
                      ? {
                            key: 'space.delete',
                            icon: Icon.Trashcan,
                            title: 'Delete',
                            trigger: () => {
                                setConfirmDialogState({
                                    space,
                                    action: 'delete',
                                    onConfirm: deleteSpace,
                                    siteId: siteContext?.siteId,
                                });
                            },
                        }
                      : null,
              ].filter(filterOutNullable);

        return { key: 'space', commands };
    });

    return {
        paletteGroup,
        dialogs: (
            <>
                <DialogRoot {...updateTitleTrigger.rootProps}>
                    <UpdateSpaceTitleDialog space={space} />
                </DialogRoot>
                <DialogRoot
                    isOpen={Boolean(confirmDialogState)}
                    onOpenChange={(isOpen) => {
                        if (!isOpen) {
                            setConfirmDialogState(null);
                        }
                    }}
                >
                    <ConfirmDialog state={confirmDialogState} />
                </DialogRoot>
                {duplicateSpace.dialog}
            </>
        ),
    };
}

/**
 * Return the command and dialog used to duplicate a space or site item.
 */
export function useDuplicateSpaceCommand(
    space: api.Space | null,
    siteContext?: DuplicateSpaceSiteContext,
    upgrade?: FeatureUpgradeAction
): {
    command: () => PaletteCommandDef | null;
    dialog: React.ReactNode;
} {
    const dialogTrigger = useDialogTriggerState();

    const canDuplicate = space
        ? siteContext
            ? // Duplicating site items creates drafts which requires edit permission
              siteContext.sitePermissions.edit
            : space.permissions.admin
        : false;
    const permissionDescription = siteContext
        ? `You need permission to edit this site to duplicate this ${siteContext.siteItemType}.`
        : 'You need admin permission on this space to duplicate it.';

    const command = useEventCallback((): PaletteCommandDef | null => {
        if (!space) {
            return null;
        }

        return {
            key: 'space.duplicate',
            icon: Icon.Duplicate,
            title: 'Duplicate',
            disabled: !canDuplicate,
            subtitle: canDuplicate ? undefined : permissionDescription,
            upgrade: Boolean(upgrade),
            trigger: upgrade?.onPress ?? dialogTrigger.open,
        };
    });

    return {
        command,
        dialog: space ? (
            <DialogRoot {...dialogTrigger.rootProps}>
                <Dialog>
                    <DuplicateSpaceDialogContent
                        space={space}
                        siteContext={
                            siteContext
                                ? {
                                      siteId: siteContext.siteId,
                                      siteSpaceId: siteContext.siteSpaceId,
                                      siteItemType: siteContext.siteItemType,
                                      canAdminSite: siteContext.sitePermissions.admin,
                                  }
                                : undefined
                        }
                    />
                </Dialog>
            </DialogRoot>
        ) : null,
    };
}

/**
 * Return space language commands.
 */
export function getSpaceLanguageCommands(space: api.Space): PaletteCommandDef | null {
    return space.permissions.admin
        ? {
              key: 'space.language',
              icon: Icon.Globe,
              title: 'Language',
              subtitle: space.language
                  ? translationLanguageDescriptors[space.language].label
                  : undefined,
              descendant: () =>
                  createPickLanguagePalette({
                      initialValue: space.language ?? null,
                      onPick: async (language) => {
                          if (language !== space.language) {
                              analytics.logEvent(
                                  analytics.createSpaceEvent(space, {
                                      type: 'space_edit_language',
                                      language,
                                  })
                              );
                              await apiClient.spaces.updateSpaceById(space.id, {
                                  language,
                              });
                          }
                      },
                  }),
          }
        : null;
}

/**
 * Return space Copy commands.
 *
 * Mainly useful for development purposes.
 */
export function useSpaceCopyCommands(space: api.Space): () => PaletteCommandDef {
    const createCopyCommands = useCopyCommands();

    return useEventCallback(() => {
        return createCopyCommands({
            id: space.id,
            title: space.title,
            target: {
                screen: {
                    type: 'space',
                    organization: space.organization,
                    space: space.id,
                },
            },
            onCopyLink: () => {
                analytics.logEvent(
                    analytics.createSpaceEvent(space, {
                        type: 'space_copy_url',
                    })
                );
            },
        });
    });
}

/**
 * Command to create spaces from a palette menu.
 */
export const useCreateSpaceCommands = (
    owner: ContentOwnership,
    site?: api.Site
): [Record<string, PaletteCommandDef | null>, React.ReactNode] => {
    const { createSpace } = useCreateInOwner(owner);
    const { navigate } = useNavigation();
    const spaceCreationDialogTrigger = useDialogTriggerState();

    const palette = React.useMemo(() => {
        return {
            newSpace: createSpace
                ? {
                      key: 'space_create',
                      icon: Icon.Space,
                      title: 'New space',
                      subtitle: 'A space for your content',
                      testId: 'sidebar.createSpaceButton',
                      trigger: () => {
                          spaceCreationDialogTrigger.setOpen(true);
                      },
                  }
                : null,
            newSpaceForSite:
                site && createSpace
                    ? {
                          key: 'new_space',
                          icon: Icon.Space,
                          title: 'New space',
                          subtitle: 'A blank space for your site content',
                          testId: 'siteContent.emptyState.createSpace',
                          trigger: async () => {
                              await toast.catch(async () => {
                                  const newSpace = await createSpace({
                                      withNavigate: false,
                                      withTitle: site.title,
                                  });

                                  await apiClient.siteSpaces.addSpaceToSite(
                                      owner.organization,
                                      site.id,
                                      {
                                          spaceId: newSpace.id,
                                      },
                                      { returnPreference: 'minimal' }
                                  );

                                  navigate({
                                      screen: {
                                          type: 'space',
                                          space: newSpace.id,
                                      },
                                  });
                              }, 'Error adding space to site');
                          },
                      }
                    : null,
        };
    }, [createSpace, navigate, owner, site, spaceCreationDialogTrigger]);

    return [
        palette,
        createSpace ? (
            <DialogRoot key="create-space-dialog" {...spaceCreationDialogTrigger.rootProps}>
                <SpaceCreationDialog
                    organizationId={owner.organization}
                    collectionId={owner.collection}
                />
            </DialogRoot>
        ) : null,
    ];
};

function MultipleSitesAlert(props: {
    siteItems: api.OrganizationAllSite[];
    title?: string;
    message: (props: { spaceSites: api.Site[] }) => React.ReactNode;
}) {
    const { siteItems, title } = props;
    const closeDialog = useDialogClose();

    if (!siteItems.length) {
        return null;
    }

    const sites = siteItems.map((siteItem) => siteItem.site);

    return (
        <Alert
            kind="danger"
            size="small"
            title={
                title ??
                `This space is linked to ${intlPluralizeString({
                    count: sites.length,
                    singular: 'a site',
                    plural: 'multiple sites',
                })}`
            }
            details={
                <div className="flex flex-col gap-2">
                    <span>{props.message({ spaceSites: sites })}</span>
                    <SpaceSitesList spaceSites={sites} onNavigate={closeDialog} />
                </div>
            }
        />
    );
}

/**
 * Render a simple list of sites where a space has a link to.
 */
export function SpaceSitesList(props: {
    spaceSites: api.Site[];
    onNavigate?: () => void;
}): React.ReactElement {
    const { spaceSites, onNavigate } = props;

    const { navigate } = useNavigation();

    return (
        <div className="flex flex-wrap gap-2">
            {spaceSites.map((spaceSite) => {
                const { id: key, title: label } = spaceSite;

                return (
                    <Button
                        key={key}
                        kind="secondary"
                        size="small"
                        leadingIcon={Icon.DocsSiteLink}
                        onPress={() => {
                            navigate({
                                screen: { type: 'site', site: key },
                            });

                            onNavigate?.();
                        }}
                        label={label}
                    />
                );
            })}
        </div>
    );
}

type DeleteOrMoveConfirmDialogState = {
    action: 'delete' | 'move';
    space: api.Space;
    onConfirm: DialogActionButtonProps['onPress'];
    /** The site the action is performed within, used to target the style guide detach. */
    siteId?: string;
};

type ConfirmDialogState = DeleteOrMoveConfirmDialogState;

function ConfirmDialog(props: { state: ConfirmDialogState | null }) {
    const { state } = props;

    const persistentState = usePersistentValue(state);

    if (!persistentState) {
        return null;
    }

    return (
        <Dialog>
            <DeleteOrMoveConfirmContent state={persistentState} />
        </Dialog>
    );
}

/**
 * This dialog is displayed to the user when they attempt to delete or move a space.
 * It may show a confirmation dialog or an alert dialog informing the user they can't
 * perform the action when the given space is referenced by a site.
 */
function DeleteOrMoveConfirmContent(props: { state: DeleteOrMoveConfirmDialogState }) {
    const { state } = props;

    const { navigate } = useNavigation();

    const { data: organizationContent } = useSuspenseQuery(
        apiQuery('getOrganizationAllContent', {
            organizationId: state.space.organization,
        })
    );

    const siteItems = useOrganizationSites(
        organizationContent ?? { sites: [], spaces: [], deletedSpaces: [] },
        { spaceId: state.space.id }
    );

    // Sites that reference this space as their style guide — a link that lives in the
    // site settings, not the content structure `useOrganizationSites` inspects.
    const styleguideSiteItems = React.useMemo(() => {
        if (state.space.type !== 'styleguide' || !organizationContent) {
            return [];
        }
        return organizationContent.sites.filter(
            (entry) => entry.site.styleguide?.space === state.space.id
        );
    }, [organizationContent, state.space.id, state.space.type]);

    // The site to detach the style guide from: the one we're acting within, or the
    // only one referencing it. With several sites and no current one, there is no
    // unambiguous target, so we block and list them instead.
    const detachTargetSite = React.useMemo(() => {
        if (!styleguideSiteItems.length) {
            return null;
        }
        const current = state.siteId
            ? styleguideSiteItems.find((entry) => entry.site.id === state.siteId)
            : undefined;
        if (current) {
            return current.site;
        }
        return styleguideSiteItems.length === 1 ? styleguideSiteItems[0].site : null;
    }, [state.siteId, styleguideSiteItems]);

    const detachStyleguide = useEventCallback(async () => {
        if (!detachTargetSite) {
            return;
        }
        await toast.catch(async () => {
            await apiClient.sites.updateSiteById(state.space.organization, detachTargetSite.id, {
                styleguide: null,
            });
            toast.success('Style guide detached');
        }, 'Error while detaching style guide');
        if (state.siteId) {
            navigate({
                screen: {
                    type: 'styleguide_new',
                    organization: state.space.organization,
                    site: state.siteId,
                },
            });
        }
    });

    const isForbiddenByStyleguide = styleguideSiteItems.length > 0;
    const isForbiddenByContent = siteItems.length > 0;
    const isActionForbidden = isForbiddenByContent || isForbiddenByStyleguide;

    return (
        <>
            <DialogHeader>
                <Heading>
                    {isActionForbidden
                        ? `Cannot ${state.action} space`
                        : `${capitalizeFirstLetter(state.action)} space`}
                </Heading>
            </DialogHeader>
            <DialogBody>
                {isForbiddenByStyleguide ? (
                    <MultipleSitesAlert
                        siteItems={styleguideSiteItems}
                        title={`This space is used as a style guide on ${intlPluralizeString({
                            count: styleguideSiteItems.length,
                            singular: 'a site',
                            plural: 'multiple sites',
                        })}`}
                        message={({ spaceSites }) => (
                            <>
                                It provides the writing rules the editing agent follows on the{' '}
                                {intlPluralizeString({
                                    count: spaceSites.length,
                                    singular: 'site',
                                    plural: 'sites',
                                })}{' '}
                                below.{' '}
                                {detachTargetSite
                                    ? 'Detach it first, then you can delete it.'
                                    : 'Detach it from each site before you can delete it.'}
                            </>
                        )}
                    />
                ) : isForbiddenByContent ? (
                    <MultipleSitesAlert
                        siteItems={siteItems}
                        message={({ spaceSites }) => (
                            <>
                                You won’t be able to {state.action} it until it's removed from the{' '}
                                {intlPluralizeString({
                                    count: spaceSites.length,
                                    singular: 'site',
                                    plural: 'sites',
                                })}{' '}
                                below.
                            </>
                        )}
                    />
                ) : state.action === 'delete' ? (
                    <>
                        This space and all of its content will be deleted. Deleted spaces can be
                        restored from the trash for up to seven days.
                    </>
                ) : (
                    <>This space and all of its content will be moved.</>
                )}
            </DialogBody>
            <DialogFooter>
                {isForbiddenByStyleguide ? (
                    detachTargetSite ? (
                        <>
                            <DialogCloseButton />
                            <DialogActionButton
                                label="Detach"
                                leadingIcon={Icon.LinkBroken}
                                onPress={detachStyleguide}
                            />
                        </>
                    ) : (
                        <DialogCloseButton label="Ok" />
                    )
                ) : isForbiddenByContent ? (
                    <DialogCloseButton label="Ok" />
                ) : (
                    <>
                        <DialogCloseButton />
                        <DialogActionButton
                            label={capitalizeFirstLetter(state.action)}
                            onPress={state.onConfirm}
                            kind="danger"
                        />
                    </>
                )}
            </DialogFooter>
        </>
    );
}

function getRenameCommandTitle(siteContext: SpaceSiteContext | undefined): string {
    if (!siteContext?.renameSiteItem) {
        return 'Rename';
    }

    switch (siteContext.siteItemType) {
        case 'section':
            return 'Rename section';
        case 'variant':
            return 'Rename variant';
        default:
            return 'Rename';
    }
}
